const DANGEROUS_TAGS = new Set([
  'script',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'style',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'frame',
  'frameset',
  'base',
  'applet',
  'svg',
  'math',
  'video',
  'audio',
  'source',
  'track',
  'img',
]);

const ALLOWED_TAGS = new Set([
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'span',
  'p',
  'small',
  'sub',
  'sup',
  'div',
]);

const URL_ATTRIBUTES = new Set(['href', 'src', 'xlink:href', 'formaction', 'action']);

export interface StripDangerousHtmlOptions {
  inline?: boolean;
}

function isDangerousUrl(value: string): boolean {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, '');
  return normalized.startsWith('javascript:')
    || normalized.startsWith('data:text/html')
    || normalized.startsWith('vbscript:');
}

function unwrapElement(element: Element): void {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

function sanitizeElement(element: Element): void {
  const tag = element.tagName.toLowerCase();

  if (DANGEROUS_TAGS.has(tag)) {
    element.remove();
    return;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    unwrapElement(element);
    return;
  }

  Array.from(element.attributes).forEach((attribute) => {
    const name = attribute.name.toLowerCase();
    if (name.startsWith('on')) {
      element.removeAttribute(attribute.name);
      return;
    }

    if (URL_ATTRIBUTES.has(name) && isDangerousUrl(attribute.value)) {
      element.removeAttribute(attribute.name);
    }
  });
}

export function stripDangerousHtml(html: string, options: StripDangerousHtmlOptions = {}): string {
  if (!html) {
    return '';
  }

  let normalizedHtml = html;
  if (options.inline) {
    // Keep shop names on one line: br becomes space, then strip all markup to text.
    normalizedHtml = normalizedHtml
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/?(p|div|li|h[1-6]|tr|td|th)[^>]*>/gi, ' ');
  } else {
    normalizedHtml = normalizedHtml.replace(/<br\s*\/?>/gi, '<br>');
  }

  const container = document.createElement('div');
  container.innerHTML = normalizedHtml;

  const elements = Array.from(container.querySelectorAll('*')).reverse();
  elements.forEach(sanitizeElement);

  if (options.inline) {
    return (container.textContent || '').replace(/\s+/g, ' ').trim();
  }

  return container.innerHTML;
}
